/* eslint-disable @typescript-eslint/no-explicit-any */
import { getEnvName, getEnvPath } from './env';
import { postcodeValidation } from 'utils';

const platform = 'my5desktop';

// The SDK script is injected asynchronously (see App.tsx), so window.c5UserService
// may not be defined yet when a UserService method is first called. Wait for it to
// appear instead of assuming it has loaded. Capped so it can never hang indefinitely.
const SDK_READY_TIMEOUT_MS = 30000;
const SDK_POLL_INTERVAL_MS = 50;

const waitForSdk = async (): Promise<void> => {
    if (window.c5UserService) return;
    await new Promise<void>((resolve, reject) => {
        let id: ReturnType<typeof setInterval>;
        const timeoutId = setTimeout(() => {
            clearInterval(id);
            reject(new Error('User Service SDK failed to load'));
        }, SDK_READY_TIMEOUT_MS);
        id = setInterval(() => {
            if (!window.c5UserService) return;
            clearTimeout(timeoutId);
            clearInterval(id);
            resolve();
        }, SDK_POLL_INTERVAL_MS);
    });
};

interface MarketingConsentValue {
    scope: 'Channel5' | 'Viacom';
    value: boolean;
}

export default class UserService {
    userService: any;
    private options: { apiUrl: string; env: string; platform: string };

    constructor() {
        const url = process.env.REACT_APP_USERSERVICE_URL;
        this.options = {
            apiUrl: url ? url.replace('{env}', getEnvPath()) : '',
            env: getEnvName(),
            platform,
        };
    }

    private init = async (): Promise<void> => {
        if (this.userService) return;
        await waitForSdk();
        if (!this.userService) this.userService = new window.c5UserService.UserService(this.options);
    };

    getVersion = async (): Promise<string> => {
        await this.init();
        return this.userService.getVersion();
    };

    signUp = async (
        email: string,
        password: string,
        givenName: string,
        familyName: string,
        birthDate: string,
        postCode: string,
        gender?: string
    ): Promise<any> => {
        await this.init();
        return this.userService.authenticationService
            .signUp(email, password, givenName, familyName, birthDate, postCode, gender)
            .catch((error: any) => error);
    };

    signIn = async (email: string, password: string, needSync: boolean = false): Promise<any> => {
        await this.init();
        return this.userService.authenticationService.signIn(email, password, needSync).catch((error: any) => error);
    };

    signOut = async (): Promise<boolean> => {
        await this.init();
        return this.userService.authenticationService.signOut().catch((error: any) => error);
    };

    forgotPassword = async (email: string): Promise<void> => {
        await this.init();
        return this.userService.authenticationService.forgotPassword(email).catch((error: any) => error);
    };

    resetPassword = async (email: string, code: string, password: string): Promise<void> => {
        await this.init();
        return this.userService.authenticationService.resetPassword(email, code, password).catch((error: any) => error);
    };

    activatePin = async (pin: string): Promise<void> => {
        await this.init();
        return this.userService.pinPairingService.activatePin(pin).catch((error: any) => error);
    };

    getUserInfo = async (): Promise<any> => {
        try {
            await this.init();
            return await this.userService.authenticationService.userInfo();
        } catch {
            return null;
        }
    };
    emailValidation = async (email: string): Promise<any> => {
        await this.init();
        return this.userService.authenticationService.validateEmail(email.toLowerCase());
    };

    // Resolves the postcode to store ('' when empty), or null when invalid. Fails closed on any SDK error.
    validatePostcode = async (postcode: string): Promise<string | null> => {
        const value = postcode.trim().toUpperCase();
        if (!value) return '';
        if (!postcodeValidation(value)) return null;
        try {
            await this.init();
            const result = await this.userService.authenticationService.validatePostcode(value);
            return result?.isValidPostcode ? result.formattedPostcode || value : null;
        } catch {
            return null;
        }
    };

    getMarketingConsentSetting = async (): Promise<any[]> => {
        await this.init();
        return this.userService.marketingConsentService.getAll();
    };

    updateMarketingConsentSettingValues = async (values: MarketingConsentValue[]): Promise<any[]> => {
        await this.init();
        return this.userService.marketingConsentService.updateValues(values);
    };
}
